import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { FinalCTA } from "@/components/home/FinalCTA";
import { SEO } from "@/components/SEO";
import { faqSchema, servicesPageSchema, generateBreadcrumbSchema } from "@/lib/structuredData";
import { useScrollReveal } from "@/hooks/use-scroll-reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Calculator,
  Users,
  FileText,
  Building2,
  Receipt,
  ClipboardList,
  UserCheck,
  Shield,
  HelpCircle,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const services = [
  {
    icon: Calculator,
    title: "Monthly Bookkeeping",
    description: "Accurate recording of all your financial transactions. We process invoices, receipts, bank statements, and keep your books up to date every month.",
  },
  {
    icon: Users,
    title: "Payroll (EMP201, EMP501)",
    description: "Complete payroll processing including monthly EMP201 submissions and bi-annual EMP501 reconciliations. We ensure your employees are paid correctly and on time.",
  },
  {
    icon: Receipt,
    title: "UIF Monthly Declarations",
    description: "Timely submission of UIF declarations to the Department of Labour. We handle the paperwork so you stay compliant with employment legislation.",
  },
  {
    icon: FileText,
    title: "Annual Financial Statements",
    description: "Professionally prepared annual financial statements that meet SARS requirements and give you a clear picture of your business performance.",
  },
  {
    icon: ClipboardList,
    title: "Annual Tax Returns",
    description: "We prepare and submit your annual income tax returns, ensuring all deductions are claimed and you remain SARS compliant.",
  },
  {
    icon: ClipboardList,
    title: "Provisional Tax Returns",
    description: "Two provisional tax submissions per year to keep you ahead of your tax obligations and avoid penalties.",
  },
  {
    icon: Building2,
    title: "CIPC Annual Returns",
    description: "On-time submission of your company's annual return to CIPC to maintain your business's good standing.",
  },
  {
    icon: UserCheck,
    title: "Beneficial Ownership (BO) Submission",
    description: "Compliance with beneficial ownership register requirements as mandated by CIPC regulations.",
  },
  {
    icon: Shield,
    title: "Workmen's Compensation",
    description: "Annual return submissions to the Compensation Fund for businesses that need to comply with COIDA requirements.",
  },
  {
    icon: HelpCircle,
    title: "Custom Quotes & Advisory",
    description: "Need something specific? We offer custom solutions and advisory services tailored to your unique business needs.",
  },
];

const faqs = [
  {
    question: "What information do you need to start?",
    answer: "We'll need your company registration documents, bank statements, previous financial records (if available), employee details for payroll, and access to any accounting software you currently use. We'll guide you through the onboarding process step by step.",
  },
  {
    question: "Do you work remotely across South Africa?",
    answer: "Yes! We serve clients throughout South Africa remotely. Our cloud-based systems allow us to work efficiently with businesses in any province, from Cape Town to Johannesburg and everywhere in between.",
  },
  {
    question: "What software do you use?",
    answer: "We work with popular accounting platforms including Xero, QuickBooks, Sage, and others. If you already have a system in place, we can likely integrate with it. If not, we'll recommend the best solution for your needs.",
  },
  {
    question: "When are monthly deadlines?",
    answer: "EMP201 submissions are due by the 7th of each month. UIF declarations are also monthly. We'll provide you with a clear compliance calendar so you always know what's coming up.",
  },
  {
    question: "Can you help if my books are behind?",
    answer: "Absolutely! We regularly help clients catch up on backlog bookkeeping. We'll assess your situation, provide a quote for the catch-up work, and get you current as quickly as possible.",
  },
];

const Services = () => {
  const { ref, isVisible } = useScrollReveal();

  return (
    <Layout>
      <SEO 
        title="Bookkeeping, Payroll & SARS Services | From R2,500/month"
        description="SAICA registered bookkeeping, payroll (EMP201, EMP501), tax returns, and SARS compliance for SA businesses. Packages from R2,500/month — get a free quote today."
        canonical="/services"
        structuredData={[faqSchema, servicesPageSchema, generateBreadcrumbSchema([
          { name: "Home", url: "https://bookkeepingservices.co.za/" },
          { name: "Services", url: "https://bookkeepingservices.co.za/services" }
        ])]}
      />

      {/* Hero */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        {/* Background with SVG curves matching homepage */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/30" />
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none opacity-60"
          viewBox="0 0 1440 600"
          preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg"
        >
          {[...Array(6)].map((_, i) => (
            <path
              key={i}
              d={`M${-50 - i * 20},${80 + i * 18} C${150 + i * 15},${20 + i * 18} ${300 - i * 8},${180 + i * 12} ${500 + i * 15},${100 + i * 18} C${700 - i * 8},${40 + i * 18} ${800 + i * 15},${200 + i * 12} ${1000 - i * 10},${140 + i * 18}`}
              fill="none"
              stroke={`hsl(173 70% 35% / ${0.06 + i * 0.012})`}
              strokeWidth="0.8"
            />
          ))}
          <path
            d="M1440,0 L1440,250 C1360,230 1300,300 1260,260 C1220,220 1280,160 1320,120 C1360,80 1400,40 1440,0Z"
            fill="hsl(173 80% 26% / 0.06)"
          />
        </svg>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-sm font-medium text-primary mb-4 tracking-wide uppercase animate-fade-up">
              What We Do
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 animate-fade-up stagger-1">
              Our Services
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-up stagger-2 max-w-2xl mx-auto">
              Comprehensive bookkeeping, payroll, and compliance services designed to keep 
              your South African business running smoothly and SARS-ready.
            </p>
            <div className="mt-8 animate-fade-up stagger-3">
              <Link to="/book-a-call">
                <Button size="lg" className="btn-primary gap-2 text-base px-8 h-14">
                  Get Started
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-28 bg-background relative">
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }} />
        <div className="container mx-auto px-4 relative z-10">
          <SectionHeader
            label="Services"
            title="Everything Your Business Needs"
            description="From daily bookkeeping to annual compliance — we cover it all."
          />

          <div
            ref={ref}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto reveal-stagger"
          >
            {services.map((service, index) => (
              <div
                key={service.title}
                className={`group relative p-8 rounded-2xl border border-border bg-card scroll-reveal-scale ${isVisible ? "revealed" : ""} hover:border-primary/30 transition-all duration-500`}
                style={{ boxShadow: 'var(--shadow-sm)' }}
              >
                {/* Hover glow */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  {/* Number + Icon row */}
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                      <service.icon className="w-6 h-6" />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground/50 uppercase tracking-widest">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <h3 className="font-heading text-lg font-semibold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 lg:py-28 bg-gradient-to-b from-muted/50 via-accent/10 to-muted/50">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="FAQ"
            title="Frequently Asked Questions"
            description="Got questions? We've got answers. Here are some common queries from our clients."
          />

          <div className="max-w-3xl mx-auto space-y-3">
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-card border border-border rounded-xl px-6 hover:border-primary/20 transition-colors duration-300 data-[state=open]:border-primary/30 data-[state=open]:shadow-md"
                >
                  <AccordionTrigger className="text-left font-heading text-base font-semibold hover:no-underline hover:text-primary transition-colors py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <FinalCTA />
    </Layout>
  );
};

export default Services;
